/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from "firebase/firestore";
import _ from "lodash";
import numeral from "numeral";
import * as utils from "@/utils";

const state = {
  // Table data
  tables: [],
  tablesRaw: [],
  currentTable: null,
  currentTableRaw: {},

  // Loading states
  loadingTables: false,
  loadingTablesRaw: false,
  loadingCurrentTable: false,
  savingTable: false,
  deletingTable: false,

  // Pending updates
  pendingTableUpdates: {},
  pendingTableRawUpdates: {},

  // Error state
  error: null
};

const actions = {
  /**
   * Load all tables for the program
   */
  async loadTables({ commit, getters }) {
    commit("setLoadingTables", true);
    commit("setTablesError", null);
    commit("setTables", []);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tablesRef = collection(programRef, "tables");
      const tablesQuery = query(tablesRef, orderBy("titleUppercase", "asc"));
      const querySnapshot = await getDocs(tablesQuery);

      const tables = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        tables.push({
          id: docSnapshot.id,
          ...data,
          selectedDatabucket: data.selectedDatabucket || "",
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setTables", tables);
      return tables;
    } catch (error) {
      console.error("Error loading tables:", error);
      commit("setTablesError", error.message);
      commit("setTables", []);
      throw error;
    } finally {
      commit("setLoadingTables", false);
    }
  },

  /**
   * Load all raw tables for the program
   */
  async loadTablesRaw({ commit, getters }) {
    commit("setLoadingTablesRaw", true);
    commit("setTablesError", null);
    commit("setTablesRaw", []);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tablesRawRef = collection(programRef, "tablesRaw");
      const querySnapshot = await getDocs(tablesRawRef);

      const tablesRaw = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        tablesRaw.push({
          id: docSnapshot.id,
          csvName: data.csvName,
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setTablesRaw", tablesRaw);
      return tablesRaw;
    } catch (error) {
      console.error("Error loading tables raw:", error);
      commit("setTablesError", error.message);
      commit("setTablesRaw", []);
      throw error;
    } finally {
      commit("setLoadingTablesRaw", false);
    }
  },

  /**
   * Load tables by databucket
   */
  async loadTablesByDatabucket({ getters, commit }, databucketId) {
    if (!databucketId) {
      throw new Error("Databucket ID is required");
    }

    commit("setLoadingTables", true);
    commit("setTablesError", null);
    commit("setTables", []);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tablesRef = collection(programRef, "tables");
      const databucketQuery = query(
        tablesRef,
        where("selectedDatabucket", "==", databucketId)
      );
      const querySnapshot = await getDocs(databucketQuery);

      const tables = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        tables.push({
          ...data,
          id: docSnapshot.id,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setTables", tables);
      return tables;
    } catch (error) {
      console.error("Error loading tables by databucket:", error);
      commit("setTablesError", error.message);
      commit("setTables", []);
      throw error;
    } finally {
      commit("setLoadingTables", false);
    }
  },

  /**
   * Load current table with raw data
   */
  async loadCurrentTable({ commit, getters }, tableId) {
    if (!tableId) {
      throw new Error("Table ID is required");
    }

    commit("setLoadingCurrentTable", true);
    commit("setCurrentTable", null);
    commit("setCurrentTableRaw", {});
    commit("clearTablePendingUpdates");
    commit("clearTableRawPendingUpdates");
    commit("setTablesError", null);

    try {
      // Load table data
      const programRef = doc(db(), "programs", getters.programId);
      const tableRef = doc(collection(programRef, "tables"), tableId);
      const tableSnapshot = await getDoc(tableRef);

      if (!tableSnapshot.exists()) {
        throw new Error("No such Table");
      }

      const tableData = tableSnapshot.data();
      const table = {
        ...tableData,
        id: tableSnapshot.id,
        participantType: tableData.participantType || "member",
        mode: tableData.mode || "dynamic",
        order: tableData.order || 0,
        cardHeaderMappings: tableData.cardHeaderMappings || [],
        runningTotalMappings: tableData.runningTotalMappings || {},
        homeCardMappings: tableData.homeCardMappings || {},
        resultsHeaderMappings: tableData.resultsHeaderMappings || [],
        remarksMappings: tableData.remarksMappings || [],
        created: tableData.created?.toDate() || new Date(),
        updated: tableData.updated?.toDate() || new Date(),
        selectedDatabucket: tableData.selectedDatabucket || ""
      };

      this.commit("setBreadCrumbDetail", table.title);

      // Load table raw data based on mode
      let tableRawRef;
      if (table.mode === "static") {
        tableRawRef = doc(collection(programRef, "tablesRaw"), tableId);
      } else {
        // Dynamic mode - get from databucket
        const databucketRef = doc(
          collection(programRef, "databuckets"),
          table.selectedDatabucket || "dummyId"
        );
        tableRawRef = doc(collection(databucketRef, "results"), "column");
      }

      const tableRawSnapshot = await getDoc(tableRawRef);
      const tableRaw = {};

      if (tableRawSnapshot.exists()) {
        const tableRawData = tableRawSnapshot.data();
        tableRaw.updated = tableRawData.updated?.toDate() || new Date();
        tableRaw.csvData = JSON.parse(tableRawData.csvData || "[]");
        tableRaw.csvName = tableRawData.csvName || "";

        if (tableRawData.runningTotalMappings) {
          table.runningTotalMappings = tableRawData.runningTotalMappings;
        }
      }

      commit("setCurrentTable", table);
      commit("setCurrentTableRaw", tableRaw);

      return { table, tableRaw };
    } catch (error) {
      console.error("Error loading current table:", error);
      commit("setTablesError", error.message);
      if (error.message === "No such Table") {
        throw error;
      }
      throw new Error("Error occurred when fetching table data");
    } finally {
      commit("setLoadingCurrentTable", false);
    }
  },

  /**
   * Get table by ID
   */
  async getTableById({ commit, getters }, tableId) {
    if (!tableId) {
      console.error("Table ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tableRef = doc(collection(programRef, "tables"), tableId);
      const tableDoc = await getDoc(tableRef);

      if (tableDoc.exists()) {
        const data = tableDoc.data();
        return {
          id: tableDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Table with ID ${tableId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting table by ID:", error);
      commit("setTablesError", error.message);
      return null;
    }
  },

  /**
   * Fake load current table (for testing/preview)
   */
  fakeLoadCurrentTable({ commit }, payload) {
    const { tableData, tableRawData } = payload;

    const table = {
      ...tableData,
      id: tableData.id,
      participantType: tableData.participantType || "member",
      mode: tableData.mode || "dynamic",
      order: tableData.order || 0,
      cardHeaderMappings: tableData.cardHeaderMappings || [],
      runningTotalMappings: tableData.runningTotalMappings || {},
      homeCardMappings: tableData.homeCardMappings || {},
      resultsHeaderMappings: tableData.resultsHeaderMappings || [],
      remarksMappings: tableData.remarksMappings || [],
      selectedDatabucket: tableData.selectedDatabucket || ""
    };

    const tableRaw = {};
    if (tableRawData) {
      tableRaw.csvData = JSON.parse(tableRawData.csvData || "[]");
      tableRaw.csvName = tableRawData.csvName || "";

      if (tableRawData.runningTotalMappings) {
        table.runningTotalMappings = tableRawData.runningTotalMappings;
      }
    }

    commit("setCurrentTable", table);
    commit("setCurrentTableRaw", tableRaw);
  },

  /**
   * Select databucket for current table
   */
  async selectDatabucket({ getters, commit }, databucketId) {
    if (!databucketId) {
      throw new Error("Databucket ID is required");
    }

    commit("setTablesError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketRef = doc(
        collection(programRef, "databuckets"),
        databucketId
      );
      const totalResultRef = doc(
        collection(databucketRef, "results"),
        "column"
      );
      const totalResultSnapshot = await getDoc(totalResultRef);

      if (!totalResultSnapshot.exists()) {
        throw new Error("Databucket result not found");
      }

      const data = totalResultSnapshot.data();

      commit("patchCurrentTable", {
        selectedDatabucket: databucketId,
        runningTotalMappings: data.runningTotalMappings || {}
      });

      commit("patchCurrentTableRaw", {
        csvData: JSON.parse(data.csvData || "[]"),
        csvName: data.csvName || "",
        updated: data.updated?.toDate() || new Date()
      });

      return data;
    } catch (error) {
      console.error("Error selecting databucket:", error);
      commit("setTablesError", error.message);
      throw new Error("Error when fetching databucket result");
    }
  },

  /**
   * Create a new table
   */
  async createTable({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Table title is required");
    }

    commit("setSavingTable", true);
    commit("setTablesError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tablesRef = collection(programRef, "tables");

      const titleUppercase = payload.title.toUpperCase();

      // Check for duplicate titles
      const duplicateQuery = query(
        tablesRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const titleDupSnapshot = await getDocs(duplicateQuery);

      if (!titleDupSnapshot.empty) {
        throw new Error("Title is already registered");
      }

      const table = {
        ...payload,
        titleUppercase,
        homeCard: {
          suffix: "of Target"
        },
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system",
        created: serverTimestamp(),
        updated: serverTimestamp()
      };

      const newTableRef = await addDoc(tablesRef, table);

      // Note: server time is unavailable until we refetch.
      const tmpTable = {
        ...payload,
        id: newTableRef.id,
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system",
        created: new Date(),
        updated: new Date()
      };

      commit("createTable", tmpTable);
      dispatch("setSnackbar", "Table Created.");

      return {
        success: true,
        tableId: newTableRef.id,
        table: tmpTable
      };
    } catch (error) {
      console.error("Error creating table:", error);
      commit("setTablesError", error.message);
      if (error.message.includes("already registered")) {
        throw error;
      }
      throw new Error("Error occurred when creating a new table");
    } finally {
      commit("setSavingTable", false);
    }
  },

  /**
   * Update table with pending changes
   */
  async updateTable({ getters, dispatch }) {
    const { hasTablePendingUpdates, hasTableRawPendingUpdates } = getters;

    if (!hasTablePendingUpdates && !hasTableRawPendingUpdates) {
      return;
    }

    try {
      if (hasTableRawPendingUpdates) {
        await Promise.all([dispatch("syncTable"), dispatch("syncTableRaw")]);
      } else if (hasTablePendingUpdates) {
        await dispatch("syncTable");
      }

      dispatch("setSnackbar", "Table Saved.");
    } catch (error) {
      console.error("Error updating table:", error);
      throw error;
    }
  },

  /**
   * Sync table data to Firestore
   */
  async syncTable({ commit, getters, state }) {
    commit("setSavingTable", true);
    commit("setTablesError", null);

    try {
      const {
        resultsHeaderMappings,
        homeCardMappings,
        cardHeaderMappings,
        remarksMappings,
        runningTotalMappings
      } = state.currentTable;

      const {
        tableAccountsMap,
        tableHeaderData,
        tableHomeData,
        tableColumnData,
        tableRemarksData
      } = getters;

      const cardHeader = cardHeaderMappings.map(item => item.label);
      const homeCard = {
        prefix: homeCardMappings.prefix || "",
        suffix: homeCardMappings.suffix || ""
      };
      const resultsHeader = resultsHeaderMappings.map(item => item.label);

      const tableData = tableAccountsMap.map(
        ({ id: accountId }, accountIndex) => {
          const homeCardData = tableHomeData[accountIndex].value;
          const headerData = tableHeaderData[accountIndex].data;
          const remarksData = tableRemarksData[accountIndex].data;
          const columnData = tableColumnData[accountIndex].data;

          const remarksDataRes = remarksMappings.reduce((result, header) => {
            return [...result, remarksData[header.column]];
          }, []);

          const columnDataRes = columnData.map(rowData => {
            const rowDataAry = resultsHeaderMappings.reduce(
              (result, header) => {
                return [...result, rowData[header.column]];
              },
              []
            );

            return {
              row: rowDataAry
            };
          });

          const headerDataRes = cardHeaderMappings.reduce((result, header) => {
            return [...result, headerData[header.column]];
          }, []);

          return {
            companyId: accountId,
            homeCardValue: homeCardData || "",
            remarks: remarksDataRes,
            totals: headerDataRes,
            rows: columnDataRes
          };
        }
      );

      const payload = {
        ...state.pendingTableUpdates,
        resultsHeaderMappings,
        resultsHeader,
        remarksMappings,
        homeCardMappings,
        homeCard,
        cardHeaderMappings,
        cardHeader,
        tableData,
        runningTotalMappings,
        updatedBy: getters.user?.id || "system"
      };

      const programRef = doc(db(), "programs", getters.programId);
      const tableRef = doc(
        collection(programRef, "tables"),
        getters.currentTable.id
      );

      await updateDoc(tableRef, {
        ...payload,
        updated: serverTimestamp()
      });

      const clientNow = new Date();
      commit("patchCurrentTable", {
        updated: clientNow
      });
      commit("clearTablePendingUpdates");

      return payload;
    } catch (error) {
      console.error("Error syncing table:", error);
      commit("setTablesError", error.message);
      throw new Error("Error when syncing table");
    } finally {
      commit("setSavingTable", false);
    }
  },

  /**
   * Sync table raw data to Firestore
   */
  async syncTableRaw({ commit, getters, state }) {
    const { pendingTableRawUpdates } = state;

    commit("setTablesError", null);

    // Note: When fetching data from databucket, there is no need
    // to update the table raw.
    if (getters.currentTable.mode === "dynamic") {
      commit("clearTableRawPendingUpdates");
      return;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tableRawRef = doc(
        collection(programRef, "tablesRaw"),
        getters.currentTable.id
      );

      await setDoc(
        tableRawRef,
        {
          ...pendingTableRawUpdates,
          csvData: JSON.stringify(pendingTableRawUpdates.csvData || []),
          updated: serverTimestamp()
        },
        { merge: true }
      );

      const clientNow = new Date();
      commit("patchCurrentTableRaw", {
        updated: clientNow
      });
      commit("clearTableRawPendingUpdates");

      return pendingTableRawUpdates;
    } catch (error) {
      console.error("Error syncing table raw:", error);
      commit("setTablesError", error.message);
      throw new Error("Error when syncing table raw");
    }
  },

  /**
   * Delete current table
   */
  async deleteTable({ commit, getters, dispatch }) {
    if (!getters.currentTable?.id) {
      throw new Error("No current table to delete");
    }

    commit("setDeletingTable", true);
    commit("setTablesError", null);

    const currentTableId = getters.currentTable.id;

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const tableRef = doc(collection(programRef, "tables"), currentTableId);
      const tableRawRef = doc(
        collection(programRef, "tablesRaw"),
        currentTableId
      );

      // Delete both table and tableRaw documents
      await Promise.all([deleteDoc(tableRef), deleteDoc(tableRawRef)]);

      commit("deleteTable", currentTableId);
      dispatch("setSnackbar", "Table deleted");

      return true;
    } catch (error) {
      console.error("Error deleting table:", error);
      commit("setTablesError", error.message);
      throw new Error("Error when deleting table");
    } finally {
      commit("setDeletingTable", false);
    }
  },

  // Note: This is only updating the store, not persisting with db
  patchCurrentTable({ commit }, payload) {
    commit("patchCurrentTable", payload);
  },

  // Note: This is only updating the store, not persisting with db
  patchCurrentTableRaw({ commit }, payload) {
    commit("patchCurrentTableRaw", payload);
  },

  // State management actions
  clearTablesError({ commit }) {
    commit("setTablesError", null);
  },

  clearCurrentTable({ commit }) {
    commit("setCurrentTable", null);
    commit("setCurrentTableRaw", {});
    commit("clearTablePendingUpdates");
    commit("clearTableRawPendingUpdates");
  }
};

const mutations = {
  setTables(state, payload) {
    state.tables = payload;
  },

  setTablesRaw(state, payload) {
    state.tablesRaw = payload;
  },

  setCurrentTable(state, payload) {
    state.currentTable = payload;
  },

  setCurrentTableRaw(state, payload) {
    state.currentTableRaw = payload;
  },

  setLoadingTables(state, payload) {
    state.loadingTables = Boolean(payload);
  },

  setLoadingTablesRaw(state, payload) {
    state.loadingTablesRaw = Boolean(payload);
  },

  setLoadingCurrentTable(state, payload) {
    state.loadingCurrentTable = Boolean(payload);
  },

  setSavingTable(state, payload) {
    state.savingTable = Boolean(payload);
  },

  setDeletingTable(state, payload) {
    state.deletingTable = Boolean(payload);
  },

  setTablesError(state, payload) {
    state.error = payload;
  },

  createTable(state, payload) {
    state.tables = [...state.tables, payload];
  },

  updateTable(state, payload) {
    state.tables = state.tables.map(item => {
      if (item.id === payload.id) {
        return { ...item, ...payload };
      }
      return item;
    });

    // Update current table if it matches
    if (state.currentTable && state.currentTable.id === payload.id) {
      state.currentTable = { ...state.currentTable, ...payload };
    }
  },

  deleteTable(state, tableId) {
    state.tables = state.tables.filter(item => item.id !== tableId);

    // Clear current table if it matches
    if (state.currentTable && state.currentTable.id === tableId) {
      state.currentTable = null;
      state.currentTableRaw = {};
      state.pendingTableUpdates = {};
      state.pendingTableRawUpdates = {};
    }
  },

  patchCurrentTable(state, payload) {
    state.currentTable = {
      ...state.currentTable,
      ...payload
    };

    state.pendingTableUpdates = {
      ...state.pendingTableUpdates,
      ...payload
    };
  },

  patchCurrentTableRaw(state, payload) {
    state.currentTableRaw = {
      ...state.currentTableRaw,
      ...payload
    };

    state.pendingTableRawUpdates = {
      ...state.pendingTableRawUpdates,
      ...payload
    };
  },

  clearTablePendingUpdates(state) {
    state.pendingTableUpdates = {};
  },

  clearTableRawPendingUpdates(state) {
    state.pendingTableRawUpdates = {};
  }
};

const getters = {
  /**
   * Get all tables
   */
  tables(state) {
    return state.tables;
  },

  /**
   * Get all raw tables
   */
  tablesRaw(state) {
    return state.tablesRaw;
  },

  /**
   * Get current table
   */
  currentTable(state) {
    return state.currentTable;
  },

  /**
   * Get current table raw data
   */
  currentTableRaw(state) {
    return state.currentTableRaw;
  },

  /**
   * Get table by ID
   */
  getTableById: state => id => {
    return state.tables.find(table => table.id === id) || null;
  },

  /**
   * Get tables by databucket
   */
  getTablesByDatabucket: state => databucketId => {
    return state.tables.filter(
      table => table.selectedDatabucket === databucketId
    );
  },

  /**
   * Get tables by participant type
   */
  getTablesByParticipantType: state => participantType => {
    return state.tables.filter(
      table => table.participantType === participantType
    );
  },

  /**
   * Get tables by mode
   */
  getTablesByMode: state => mode => {
    return state.tables.filter(table => table.mode === mode);
  },

  /**
   * Get loading states
   */
  loadingTables(state) {
    return state.loadingTables;
  },

  loadingTablesRaw(state) {
    return state.loadingTablesRaw;
  },

  loadingCurrentTable(state) {
    return state.loadingCurrentTable;
  },

  savingTable(state) {
    return state.savingTable;
  },

  deletingTable(state) {
    return state.deletingTable;
  },

  /**
   * Get error state
   */
  tablesError(state) {
    return state.error;
  },

  /**
   * Check if any table operations are loading
   */
  tablesIsLoading(state) {
    return (
      state.loadingTables ||
      state.loadingTablesRaw ||
      state.loadingCurrentTable ||
      state.savingTable ||
      state.deletingTable
    );
  },

  /**
   * Get tables count
   */
  tablesCount(state) {
    return state.tables.length;
  },

  /**
   * Get static tables count
   */
  staticTablesCount(state) {
    return state.tables.filter(table => table.mode === "static").length;
  },

  /**
   * Get dynamic tables count
   */
  dynamicTablesCount(state) {
    return state.tables.filter(table => table.mode === "dynamic").length;
  },

  /**
   * Check if tables exist
   */
  hasTables(state) {
    return state.tables.length > 0;
  },

  /**
   * Get tables as options for dropdowns
   */
  tablesOptions(state) {
    return state.tables.map(table => ({
      text: table.title,
      value: table.id
    }));
  },

  /**
   * Get tables by participant type as options
   */
  getTablesOptionsByParticipantType: state => participantType => {
    return state.tables
      .filter(table => table.participantType === participantType)
      .map(table => ({
        text: table.title,
        value: table.id
      }));
  },

  /**
   * Get recent tables (last 10)
   */
  recentTables(state) {
    return [...state.tables]
      .sort((a, b) => new Date(b.updated) - new Date(a.updated))
      .slice(0, 10);
  },

  /**
   * Search tables by title
   */
  searchTables: state => searchTerm => {
    if (!searchTerm) return state.tables;

    const term = searchTerm.toLowerCase();
    return state.tables.filter(
      table =>
        table.title.toLowerCase().includes(term) ||
        (table.description && table.description.toLowerCase().includes(term))
    );
  },

  /**
   * Get table statistics
   */
  tableStatistics(state) {
    const total = state.tables.length;
    const staticTables = state.tables.filter(table => table.mode === "static")
      .length;
    const dynamicTables = state.tables.filter(table => table.mode === "dynamic")
      .length;
    const memberTables = state.tables.filter(
      table => table.participantType === "member"
    ).length;
    const companyTables = state.tables.filter(
      table => table.participantType === "company"
    ).length;

    return {
      total,
      staticTables,
      dynamicTables,
      memberTables,
      companyTables
    };
  },

  // Existing complex getters for table data processing
  tableEntityMap(state, getters) {
    return getters.currentTable?.participantType === "member"
      ? getters.memberAccountKeysMap
      : getters.companyAccountKeysMap;
  },

  tableCsvHeaders(state) {
    const { csvData } = state.currentTableRaw;
    if (!csvData || !csvData.length) {
      return [];
    }

    return (csvData[0] || []).map(header => {
      // Note: We have reserved word 'Percentage'
      // If we find the same one in the csv, append trailing space so that
      // It doesn't conflict with reserved functionality
      if (header === "Percentage") {
        return "Percentage ";
      }
      return header;
    });
  },

  tableCsvBody(state, getters) {
    const { csvData } = state.currentTableRaw;
    if (!csvData || !csvData.length) {
      return [];
    }

    return csvData.slice(1).map(row => {
      return getters.tableCsvHeaders.reduce(
        (rowResult, header, headerIndex) => {
          return {
            ...rowResult,
            [header]: row[headerIndex]
          };
        },
        {}
      );
    });
  },

  tableSystemHeaders(state) {
    const calculated = ["Percentage"];

    return state.currentTable?.participantType === "member"
      ? ["Full Name", ...calculated]
      : ["Company Title", ...calculated];
  },

  tableMatchingData(state, getters) {
    const { accountKeyColumn } = state.currentTable || {};
    const { tableCsvBody, tableEntityMap } = getters;

    if (!tableCsvBody.length || !accountKeyColumn) {
      return [];
    }

    return tableCsvBody.filter(item => {
      const accountKeyValue = item[accountKeyColumn];
      return !!(
        accountKeyValue &&
        tableEntityMap &&
        tableEntityMap[accountKeyValue]
      );
    });
  },

  tableAccountsMap(state, getters) {
    const { accountKeyColumn } = state.currentTable || {};
    const { tableMatchingData, tableEntityMap } = getters;

    if (!tableMatchingData.length || !accountKeyColumn) {
      return [];
    }

    const allAccountKeyValues = tableMatchingData.map(
      item => item[accountKeyColumn]
    );
    const uniqAccountKeyValues = _.uniq(allAccountKeyValues);

    return uniqAccountKeyValues
      .map(item => {
        const entity = tableEntityMap && tableEntityMap[item];
        if (!entity) return null;

        const label = entity.title;

        return {
          label,
          value: item,
          ...entity
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.titleUppercase < b.titleUppercase) {
          return -1;
        }
        if (a.titleUppercase > b.titleUppercase) {
          return 1;
        }
        return 0;
      });
  },

  tableHeaderData(state, getters) {
    const currentTable = state.currentTable;
    if (!currentTable) return [];

    const {
      accountKeyColumn,
      cardHeaderMappings,
      runningTotalMappings: {
        column: runningTotalColumn,
        row: runningTotalRow
      } = {}
    } = currentTable;

    const { tableCsvBody, tableAccountsMap } = getters;

    const specialColumns = ["Company Title", "Full Name", "Percentage"];

    return tableAccountsMap.map(
      ({ value: accountKey, label: accountLabel }) => {
        const rawData = tableCsvBody.filter(
          row => row[accountKeyColumn] === accountKey
        );

        // Filter the data by running total
        let filteredData = rawData;
        if (runningTotalColumn && runningTotalRow) {
          ({ filtered: filteredData } = rawData.reduce(
            (result, item) => {
              if (result.foundTarget) {
                return result;
              }
              const val = item[runningTotalColumn];
              if (val === runningTotalRow) {
                return {
                  foundTarget: true,
                  filtered: [...result.filtered, item]
                };
              }
              return {
                foundTarget: false,
                filtered: [...result.filtered, item]
              };
            },
            {
              filtered: [],
              foundTarget: false
            }
          ));
        }

        // Get the aggregated values
        // At this stage, percent value will just be blank

        let percentMappingIndex = -1;
        const data = {};
        (cardHeaderMappings || []).forEach((mapping, mappingIndex) => {
          const { type, column, format, rounding } = mapping;
          let value;
          if (specialColumns.includes(column)) {
            if (column === "Percentage") {
              value = "";
              percentMappingIndex = mappingIndex;
            } else {
              value = accountLabel;
            }
          } else if (type !== "number") {
            value = filteredData[0] ? filteredData[0][column] : "";
          } else {
            const sum = filteredData.reduce((result, item) => {
              // Note: Remove all special characters except numbers, '.' and '-'
              const itemVal = utils.convertToNumber(item[column]);
              return result + itemVal;
            }, 0);

            const rounded = numeral(sum).format(rounding);
            const isAppend = format !== "%";
            value = isAppend ? `${format}${rounded}` : `${rounded}${format}`;
          }

          data[column] = value;
        });

        // There is a "Percentage Column", we should calculate percent value
        if (percentMappingIndex !== -1) {
          const percentMapping = cardHeaderMappings[percentMappingIndex];
          const { column, leftOperator, rightOperator } = percentMapping;
          const { leftSum, rightSum } = filteredData.reduce(
            (result, item) => {
              return {
                leftSum:
                  result.leftSum + utils.convertToNumber(item[leftOperator]),
                rightSum:
                  result.rightSum + utils.convertToNumber(item[rightOperator])
              };
            },
            {
              leftSum: 0,
              rightSum: 0
            }
          );

          const percentValue = (leftSum / rightSum) * 100;
          const value = `${numeral(percentValue).format("0.0")}%`;
          data[column] = value;
        }

        return {
          accountKey,
          data
        };
      }
    );
  },

  tableHomeData(state, getters) {
    const currentTable = state.currentTable;
    if (!currentTable) return [];

    const {
      accountKeyColumn,
      homeCardMappings,
      runningTotalMappings: {
        column: runningTotalColumn,
        row: runningTotalRow
      } = {}
    } = currentTable;

    const { tableCsvBody, tableAccountsMap } = getters;

    const specialColumns = ["Company Title", "Full Name", "Percentage"];

    return tableAccountsMap.map(
      ({ value: accountKey, label: accountLabel }) => {
        const rawData = tableCsvBody.filter(
          row => row[accountKeyColumn] === accountKey
        );

        // Filter the data by running total
        let filteredData = rawData;
        if (runningTotalColumn && runningTotalRow) {
          ({ filtered: filteredData } = rawData.reduce(
            (result, item) => {
              if (result.foundTarget) {
                return result;
              }
              const val = item[runningTotalColumn];
              if (val === runningTotalRow) {
                return {
                  foundTarget: true,
                  filtered: [...result.filtered, item]
                };
              }
              return {
                foundTarget: false,
                filtered: [...result.filtered, item]
              };
            },
            {
              filtered: [],
              foundTarget: false
            }
          ));
        }

        // Get the aggregated values
        // At this stage, percent value will just be blank
        const { type, column, format, rounding } = homeCardMappings || {};

        let value;
        if (specialColumns.includes(column)) {
          if (column === "Percentage") {
            value = "";
          } else {
            value = accountLabel;
          }
        } else if (type !== "number") {
          value = filteredData[0] ? filteredData[0][column] : "";
        } else {
          const sum = filteredData.reduce((result, item) => {
            // Note: Remove all special characters except numbers, '.' and '-'
            const itemVal = utils.convertToNumber(item[column]);
            return result + itemVal;
          }, 0);

          const rounded = numeral(sum).format(rounding);
          const isAppend = format !== "%";
          value = isAppend ? `${format}${rounded}` : `${rounded}${format}`;
        }

        // There is a "Percentage Column", we should calculate percent value
        if (column === "Percentage") {
          const { leftOperator, rightOperator } = homeCardMappings || {};

          const { leftSum, rightSum } = filteredData.reduce(
            (result, item) => {
              return {
                leftSum:
                  result.leftSum + utils.convertToNumber(item[leftOperator]),
                rightSum:
                  result.rightSum + utils.convertToNumber(item[rightOperator])
              };
            },
            {
              leftSum: 0,
              rightSum: 0
            }
          );

          const percentValue = (leftSum / rightSum) * 100;
          value = `${numeral(percentValue).format("0.0")}%`;
        }

        return {
          accountKey,
          value
        };
      }
    );
  },

  tableColumnData(state, getters) {
    const currentTable = state.currentTable;
    if (!currentTable) return [];

    const { accountKeyColumn, resultsHeaderMappings } = currentTable;

    const { tableCsvBody, tableAccountsMap } = getters;

    const specialColumns = ["Company Title", "Full Name"];

    return tableAccountsMap.map(
      ({ value: accountKey, label: accountLabel }) => {
        const rawData = tableCsvBody.filter(
          row => row[accountKeyColumn] === accountKey
        );

        const data = [];
        rawData.forEach((row, rowIndex) => {
          const rowData = {};
          (resultsHeaderMappings || []).forEach(mapping => {
            let value;
            const { type, column, format, rounding } = mapping;
            if (specialColumns.includes(column)) {
              value = accountLabel;
            } else if (type !== "number") {
              value = rawData[rowIndex] ? rawData[rowIndex][column] : "";
            } else {
              const number = utils.convertToNumber(
                rawData[rowIndex] ? rawData[rowIndex][column] : 0
              );
              const rounded = numeral(number).format(rounding);
              const isAppend = format !== "%";
              value = isAppend ? `${format}${rounded}` : `${rounded}${format}`;
            }
            rowData[column] = value;
          });

          data.push(rowData);
        });

        return {
          accountKey,
          data
        };
      }
    );
  },

  tableRemarksData(state, getters) {
    const currentTable = state.currentTable;
    if (!currentTable) return [];

    const { accountKeyColumn, remarksMappings } = currentTable;

    const { tableCsvBody, tableAccountsMap } = getters;

    return tableAccountsMap.map(({ value: accountKey }) => {
      const rawData = tableCsvBody.filter(
        row => row[accountKeyColumn] === accountKey
      );
      const data = {};

      (remarksMappings || []).forEach(mapping => {
        const value = rawData[rawData.length - 1]
          ? rawData[rawData.length - 1][mapping.column]
          : "";
        data[mapping.column] = value;
      });

      return {
        accountKey,
        data
      };
    });
  },

  hasTablePendingUpdates(state) {
    return Object.keys(state.pendingTableUpdates).length !== 0;
  },

  hasTableRawPendingUpdates(state) {
    return Object.keys(state.pendingTableRawUpdates).length !== 0;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
